import React, { useEffect, useState, useRef } from 'react';
import { ScrollRestoration } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import PageHero from './PageHero';
import AnimateOnScroll from './AnimateOnScroll';
import 'animate.css';

const FALLBACK_PRICES = [
    { name: 'Natural 95', price: '1.599', icon: '⛽' },
    { name: 'Diesel', price: '1.549', icon: '🛢️' },
    { name: 'Natural 98', price: '1.699', icon: '⚡' },
];

const SERVICES = [
    { icon: '⛽', title: 'Nafta & Benzín', desc: 'Slovnaft palivá najvyššej kvality.' },
    { icon: '💧', title: 'AdBlue', desc: 'Pre dieselové vozidlá s technológiou SCR.' },
    { icon: '💨', title: 'Stlačený vzduch', desc: 'Dohuštenie pneumatík zadarmo.' },
    { icon: '💳', title: 'Platba kartou', desc: 'Akceptujeme všetky platobné karty.' },
    { icon: '📅', title: 'Otvorené každý deň', desc: 'Sme k dispozícii 7 dní v týždni.' },
    { icon: '🚛', title: 'Veľkokapacitné čerpanie', desc: 'Vhodné pre nákladné vozidlá a techniku.' },
];

const HOURS = [
    { day: 'Pondelok – Nedela', time: '06:00 – 22:00' },
];

function useCountUp(target, duration = 1200, active = false) {
    const [display, setDisplay] = useState('0.000');
    useEffect(() => {
        if (!active) return;
        const num = parseFloat(target);
        if (isNaN(num)) { setDisplay(target); return; }
        const steps = 40;
        const interval = duration / steps;
        let step = 0;
        const timer = setInterval(() => {
            step++;
            const val = (num * step) / steps;
            setDisplay(val.toFixed(3));
            if (step >= steps) { clearInterval(timer); setDisplay(num.toFixed(3)); }
        }, interval);
        return () => clearInterval(timer);
    }, [target, duration, active]);
    return display;
}

function PriceCard({ icon, name, price, isFallback, active }) {
    const displayPrice = useCountUp(price, 1200, active);
    return (
        <div className="flex flex-col items-center bg-gray-800 rounded-2xl px-8 py-8 shadow-xl border border-gray-700 min-w-[180px]">
            <span className="text-4xl mb-3">{icon}</span>
            <span className="text-gray-400 text-sm font-medium uppercase tracking-widest mb-2">{name}</span>
            <span className="text-white font-extrabold text-4xl tracking-tight">
                {displayPrice} <span className="text-xl font-semibold text-gray-300">€/l</span>
            </span>
            <span className="mt-3 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full">Slovnaft</span>
            {isFallback && (
                <span className="mt-2 text-gray-500 text-xs italic">orientačná cena</span>
            )}
        </div>
    );
}

function CerpaciaStanica() {
    const [prices, setPrices] = useState(null);
    const [isFallback, setIsFallback] = useState(false);
    const [updatedDate, setUpdatedDate] = useState(null);
    const [pricesVisible, setPricesVisible] = useState(false);
    const pricesSectionRef = useRef(null);

    useEffect(() => {
        fetch('https://www.fuel-prices.eu/api/v1/sk')
            .then((r) => r.json())
            .then((data) => {
                const fuels = data?.data?.fuels ?? data?.fuels ?? [];
                const find = (names) => {
                    for (const n of names) {
                        const match = fuels.find((f) =>
                            f.fuel_type?.toLowerCase().includes(n.toLowerCase()) ||
                            f.name?.toLowerCase().includes(n.toLowerCase())
                        );
                        if (match) return match;
                    }
                    return null;
                };

                const diesel = find(['diesel', 'nafta']);
                const n95 = find(['natural 95', 'benzín 95', '95']);
                const n98 = find(['natural 98', '98']);

                const result = [];
                if (n95) result.push({ name: 'Natural 95', price: parseFloat(n95.price ?? n95.average).toFixed(3), icon: '⛽' });
                if (diesel) result.push({ name: 'Diesel', price: parseFloat(diesel.price ?? diesel.average).toFixed(3), icon: '🛢️' });
                if (n98) result.push({ name: 'Natural 98', price: parseFloat(n98.price ?? n98.average).toFixed(3), icon: '⚡' });

                if (result.length > 0) {
                    setPrices(result);
                    const dateStr = data?.data?.date ?? data?.date ?? data?.updated_at;
                    if (dateStr) {
                        const d = new Date(dateStr);
                        setUpdatedDate(d.toLocaleDateString('sk-SK', { day: '2-digit', month: '2-digit', year: 'numeric' }));
                    }
                } else {
                    throw new Error('no prices');
                }
            })
            .catch(() => {
                setPrices(FALLBACK_PRICES);
                setIsFallback(true);
            });
    }, []);

    useEffect(() => {
        if (!pricesSectionRef.current) return;
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setPricesVisible(true); },
            { threshold: 0.2 }
        );
        observer.observe(pricesSectionRef.current);
        return () => observer.disconnect();
    }, []);

    const scrollToMap = () => {
        document.getElementById('mapa-stanica')?.scrollIntoView({ behavior: 'smooth' });
    };

    const displayPrices = prices ?? FALLBACK_PRICES;

    return (
        <div className="flex flex-col min-h-screen">
            <Navbar />
            <PageHero
                title="Čerpacia"
                highlight="stanica"
                subtitle="Tankujte u nás – Slovnaft palivá, rýchlo a spoľahlivo."
            />

            {/* Fuel prices band */}
            <section ref={pricesSectionRef} className="bg-gray-900 py-14 px-6">
                <div className="max-w-screen-xl mx-auto">
                    <h2 className="text-center text-white font-bold text-3xl mb-2">
                        Aktuálne ceny palív
                    </h2>
                    <p className="text-center text-gray-400 text-sm mb-10">
                        Priemerné ceny palív v SR (Slovnaft) — presná cena na stojane sa môže mierne líšiť.
                        {updatedDate && !isFallback && (
                            <span className="ml-2 text-gray-500">Aktualizované: {updatedDate}</span>
                        )}
                    </p>
                    <div className="flex flex-wrap justify-center gap-6">
                        {displayPrices.map((p, i) => (
                            <PriceCard key={i} {...p} isFallback={isFallback} active={pricesVisible} />
                        ))}
                    </div>
                    {isFallback && (
                        <p className="text-center text-gray-600 text-xs mt-6">
                            Ceny sú orientačné – aktuálne ceny overte priamo na čerpacej stanici.
                        </p>
                    )}
                </div>
            </section>

            {/* About section */}
            <section className="bg-white py-20 px-6">
                <div className="max-w-screen-xl mx-auto flex flex-col lg:flex-row gap-12 items-center">
                    <AnimateOnScroll className="flex-1">
                        <h2 className="text-4xl font-bold text-gray-800 mb-4">
                            Čerpacia stanica <span className="text-red-600">Petromin</span>
                        </h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            Naša čerpacia stanica v Giraltovciach je partnerskou stanicou siete{' '}
                            <strong>Slovnaft</strong>. Ponúkame kvalitné palivá pre osobné aj nákladné
                            vozidlá, stavebnú a poľnohospodársku techniku.
                        </p>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            Okrem palív nájdete u nás AdBlue, stlačený vzduch na dohuštenie pneumatík
                            a rýchlu obsluhu bez čakania. Stanica je otvorená každý deň.
                        </p>
                        <p className="text-gray-600 leading-relaxed mb-6">
                            Sídlime priamo v areáli Petromin s.r.o. — môžete prísť natankovať a zároveň
                            si objednať stavebný materiál alebo dopravu.
                        </p>
                        <button
                            onClick={scrollToMap}
                            className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-3 rounded transition-colors duration-200"
                        >
                            Zobraziť na mape
                        </button>
                    </AnimateOnScroll>
                    <AnimateOnScroll className="flex-1 w-full" delay={150}>
                        <img
                            src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=700&q=80"
                            alt="Čerpacia stanica"
                            className="w-full rounded-2xl shadow-2xl object-cover max-h-80"
                            loading="lazy"
                        />
                    </AnimateOnScroll>
                </div>
            </section>

            {/* Services section */}
            <section className="bg-gray-50 py-20 px-6">
                <div className="max-w-screen-xl mx-auto">
                    <AnimateOnScroll>
                        <h2 className="text-center font-bold text-4xl text-gray-800 mb-2">Naše služby</h2>
                        <p className="text-center text-gray-500 mb-12 text-sm">
                            Všetko čo potrebujete na jednom mieste.
                        </p>
                    </AnimateOnScroll>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {SERVICES.map((s, i) => (
                            <AnimateOnScroll key={i} delay={i * 80}>
                                <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex items-start gap-4">
                                    <span className="text-3xl mt-1">{s.icon}</span>
                                    <div>
                                        <h3 className="font-bold text-gray-800 text-lg mb-1">{s.title}</h3>
                                        <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
                                    </div>
                                </div>
                            </AnimateOnScroll>
                        ))}
                    </div>
                </div>
            </section>

            {/* Opening hours */}
            <section className="bg-white py-20 px-6">
                <div className="max-w-md mx-auto">
                    <AnimateOnScroll>
                        <h2 className="text-center font-bold text-4xl text-gray-800 mb-10">Otváracie hodiny</h2>
                        <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100">
                            <div className="bg-red-600 text-white grid grid-cols-2 px-6 py-3 font-semibold text-sm uppercase tracking-wider">
                                <span>Deň</span>
                                <span className="text-right">Hodiny</span>
                            </div>
                            {HOURS.map((h, i) => (
                                <div
                                    key={i}
                                    className={`grid grid-cols-2 px-6 py-4 text-sm ${i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
                                >
                                    <span className="font-medium text-gray-700">{h.day}</span>
                                    <span className="text-right font-bold text-gray-900">{h.time}</span>
                                </div>
                            ))}
                        </div>
                    </AnimateOnScroll>
                </div>
            </section>

            {/* Map section */}
            <section id="mapa-stanica" className="bg-gray-50 py-20 px-6">
                <div className="max-w-screen-xl mx-auto">
                    <AnimateOnScroll>
                        <h2 className="text-center font-bold text-4xl text-gray-800 mb-10">Kde nás nájdete</h2>
                    </AnimateOnScroll>
                    <AnimateOnScroll delay={100}>
                        <iframe
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1305.9504909034154!2d21.505501604674382!3d49.107521521468314!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x473e97030911bc1b%3A0xb3a5606c0042e89b!2sPETROMIN%2C%20spol.%20s.r.o.!5e0!3m2!1ssk!2ssk!4v1717443646955!5m2!1ssk!2ssk"
                            width="100%"
                            height="450"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            className="rounded-2xl shadow-2xl w-full"
                            title="Mapa čerpacej stanice Petromin"
                        />
                    </AnimateOnScroll>
                </div>
            </section>

            <Footer />
            <ScrollRestoration />
        </div>
    );
}

export default CerpaciaStanica;
